import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, KeyRound, RefreshCw, UserPlus, Check, Edit3, Trash2,
  Copy, Loader2, Mail, Shield,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin';

const ROLE_COLORS = {
  owner:      { bg: 'rgba(201,168,76,0.12)', color: '#c9a84c' },
  admin:      { bg: 'rgba(124,58,237,0.12)', color: '#7c3aed' },
  superadmin: { bg: 'rgba(220,38,38,0.12)',  color: '#dc2626' },
};

function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || { bg: 'rgba(100,100,100,0.1)', color: '#666' };
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
      style={{ background: c.bg, color: c.color }}>
      {role}
    </span>
  );
}

/**
 * Modal che consente al SuperAdmin di:
 * - vedere gli utenti admin di un tenant (email, nome, ruolo, ultimo accesso)
 * - modificare email / nome / stato
 * - resettare la password (auto-generata e mostrata una tantum)
 * - creare un nuovo utente admin di soccorso
 * - eliminare un utente admin (se non è l'unico)
 */
export function TenantAccessModal({ tenant, storedKey, onClose }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', password: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [revealPwd, setRevealPwd] = useState(null); // {userId, email, password}

  const headers = { 'X-Superadmin-Key': storedKey };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tenants/${tenant.slug}/admins`, { headers });
      setAdmins(res.data.admins || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento utenti.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.slug, storedKey]);

  useEffect(() => { load(); }, [load]);

  const resetPassword = async (u) => {
    if (!window.confirm(`Generare una nuova password per ${u.email}?`)) return;
    try {
      const res = await axios.put(
        `${API}/tenants/${tenant.slug}/admins/${u.id}`,
        { reset_password: true }, { headers },
      );
      if (res.data.generated_password) {
        setRevealPwd({ userId: u.id, email: u.email, password: res.data.generated_password });
      }
      toast.success('Password rigenerata.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore.');
    }
  };

  const setCustomPassword = async (u) => {
    const pwd = window.prompt(`Nuova password per ${u.email} (min 8 caratteri):`);
    if (!pwd) return;
    if (pwd.length < 8) return toast.error('Password troppo corta (min 8).');
    try {
      await axios.put(
        `${API}/tenants/${tenant.slug}/admins/${u.id}`,
        { password: pwd }, { headers },
      );
      toast.success('Password aggiornata.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore.');
    }
  };

  const changeEmail = async (u) => {
    const email = window.prompt('Nuova email:', u.email);
    if (!email || email === u.email) return;
    try {
      await axios.put(
        `${API}/tenants/${tenant.slug}/admins/${u.id}`,
        { email }, { headers },
      );
      toast.success('Email aggiornata.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore.');
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(
        `${API}/tenants/${tenant.slug}/admins/${u.id}`,
        { is_active: !u.is_active }, { headers },
      );
      toast.success(u.is_active ? 'Utente disattivato.' : 'Utente attivato.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore.');
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}? Non potrà più accedere al pannello.`)) return;
    try {
      await axios.delete(`${API}/tenants/${tenant.slug}/admins/${u.id}`, { headers });
      toast.success('Utente eliminato.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore.');
    }
  };

  const createAdmin = async () => {
    if (!newAdmin.email) return toast.error('Email obbligatoria.');
    if (newAdmin.password && newAdmin.password.length < 8) return toast.error('Password troppo corta.');
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/tenants/${tenant.slug}/admins`,
        {
          email: newAdmin.email.trim().toLowerCase(),
          name: newAdmin.name.trim() || undefined,
          password: newAdmin.password || undefined,
        }, { headers },
      );
      if (res.data.generated_password) {
        setRevealPwd({ userId: res.data.id, email: res.data.email, password: res.data.generated_password });
      }
      toast.success('Utente admin creato.');
      setNewAdmin({ email: '', name: '', password: '' });
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione.');
    } finally {
      setCreating(false);
    }
  };

  const copyPwd = (pwd) => {
    navigator.clipboard.writeText(pwd);
    toast.success('Password copiata negli appunti.');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" style={{ background: 'rgba(0,0,0,0.8)' }} data-testid="tenant-access-modal">
      <div className="w-full max-w-2xl rounded-sm overflow-hidden flex flex-col"
        style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)', maxHeight: '90vh' }}>

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between shrink-0"
          style={{ borderBottom: '1px solid var(--lux-border-1)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm flex items-center justify-center"
              style={{ background: 'rgba(201,168,76,0.12)', color: '#c9a84c' }}>
              <KeyRound size={16} />
            </div>
            <div>
              <h3 className="font-serif text-lg" style={{ color: 'var(--lux-text)' }}>
                Accessi Tenant
              </h3>
              <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
                {tenant.name} · <code>{tenant.slug}</code>
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-modal-btn"
            className="p-2 rounded hover:opacity-80"
            style={{ color: 'var(--lux-text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {revealPwd && (
            <div className="mb-5 p-4 rounded-sm" data-testid="reveal-password-box"
              style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
              <div className="flex items-start gap-3">
                <Shield size={16} style={{ color: '#c9a84c' }} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#c9a84c' }}>
                    Nuova password per {revealPwd.email}
                  </p>
                  <p className="text-[11px] mb-2" style={{ color: 'var(--lux-text-muted)' }}>
                    Copia e comunica al cliente — non verrà più mostrata dopo aver chiuso questa finestra.
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 rounded-sm font-mono text-sm break-all"
                      style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-gold)', border: '1px solid var(--lux-border-2)' }}>
                      {revealPwd.password}
                    </code>
                    <button onClick={() => copyPwd(revealPwd.password)}
                      className="px-3 py-2 rounded-sm text-xs flex items-center gap-1.5"
                      style={{ background: 'var(--lux-gold)', color: '#000' }}
                      data-testid="copy-password-btn">
                      <Copy size={12} /> Copia
                    </button>
                  </div>
                  <button onClick={() => setRevealPwd(null)}
                    className="mt-3 text-[10px] uppercase tracking-widest"
                    style={{ color: 'var(--lux-text-muted)' }}>
                    Ho copiato la password, nascondi
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
              {admins.length} utente{admins.length === 1 ? '' : 'i'} admin del tenant
            </p>
            <div className="flex gap-2">
              <button onClick={load} className="p-2 rounded-sm" data-testid="refresh-admins-btn"
                style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                <RefreshCw size={12} />
              </button>
              <button onClick={() => setShowCreate(v => !v)}
                data-testid="toggle-create-admin-btn"
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-sm"
                style={{ background: 'var(--lux-gold)', color: '#000' }}>
                <UserPlus size={12} /> Nuovo Accesso
              </button>
            </div>
          </div>

          {/* Create form */}
          {showCreate && (
            <div className="mb-5 p-4 rounded-sm space-y-3" data-testid="create-admin-form"
              style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>
                Crea utente admin di soccorso
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input type="email" placeholder="Email" value={newAdmin.email}
                  onChange={e => setNewAdmin(p => ({ ...p, email: e.target.value }))}
                  data-testid="new-admin-email"
                  className="px-3 py-2 text-sm rounded-sm"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
                <input type="text" placeholder="Nome (opzionale)" value={newAdmin.name}
                  onChange={e => setNewAdmin(p => ({ ...p, name: e.target.value }))}
                  data-testid="new-admin-name"
                  className="px-3 py-2 text-sm rounded-sm"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
              </div>
              <input type="text" placeholder="Password (lascia vuoto per generarla automaticamente)"
                value={newAdmin.password}
                onChange={e => setNewAdmin(p => ({ ...p, password: e.target.value }))}
                data-testid="new-admin-password"
                className="w-full px-3 py-2 text-sm rounded-sm"
                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
              <div className="flex gap-2">
                <button onClick={createAdmin} disabled={creating}
                  data-testid="submit-create-admin"
                  className="flex-1 py-2 text-sm font-semibold rounded-sm"
                  style={{ background: 'var(--lux-gold)', color: '#000' }}>
                  {creating ? 'Creazione...' : 'Crea utente'}
                </button>
                <button onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm rounded-sm"
                  style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                  Annulla
                </button>
              </div>
            </div>
          )}

          {/* Users list */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin" size={20} style={{ color: 'var(--lux-gold)' }} />
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-10 text-sm" style={{ color: 'var(--lux-text-muted)' }}>
              Nessun utente admin registrato per questo tenant.
            </div>
          ) : (
            <div className="space-y-2">
              {admins.map(u => (
                <div key={u.id} data-testid={`admin-row-${u.id}`}
                  className="p-3 rounded-sm"
                  style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)', opacity: u.is_active ? 1 : 0.55 }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-sm flex items-center justify-center font-bold text-sm shrink-0"
                      style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-gold)' }}>
                      {(u.name || u.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>
                          {u.name || '—'}
                        </span>
                        <RoleBadge role={u.role} />
                        {!u.is_active && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full"
                            style={{ background: 'rgba(100,100,100,0.15)', color: '#888' }}>
                            disattivato
                          </span>
                        )}
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
                        <Mail size={10} className="inline mr-1" />
                        {u.email}
                      </p>
                      {u.last_login_at && (
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>
                          Ultimo accesso: {new Date(u.last_login_at).toLocaleString('it-IT')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pl-11">
                    <button onClick={() => resetPassword(u)}
                      data-testid={`reset-pwd-btn-${u.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px]"
                      style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }}>
                      <KeyRound size={11} /> Genera Password
                    </button>
                    <button onClick={() => setCustomPassword(u)}
                      data-testid={`custom-pwd-btn-${u.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px]"
                      style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}>
                      <Shield size={11} /> Password Manuale
                    </button>
                    <button onClick={() => changeEmail(u)}
                      data-testid={`change-email-btn-${u.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px]"
                      style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}>
                      <Edit3 size={11} /> Cambia Email
                    </button>
                    <button onClick={() => toggleActive(u)}
                      data-testid={`toggle-active-btn-${u.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px]"
                      style={{
                        background: u.is_active ? 'rgba(100,100,100,0.1)' : 'rgba(5,150,105,0.1)',
                        color: u.is_active ? '#888' : '#059669',
                        border: `1px solid ${u.is_active ? 'var(--lux-border-2)' : 'rgba(5,150,105,0.3)'}`
                      }}>
                      {u.is_active ? <X size={11} /> : <Check size={11} />}
                      {u.is_active ? 'Disattiva' : 'Riattiva'}
                    </button>
                    <button onClick={() => removeUser(u)}
                      data-testid={`delete-admin-btn-${u.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px]"
                      style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.25)' }}>
                      <Trash2 size={11} /> Elimina
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 shrink-0 flex items-center justify-between"
          style={{ borderTop: '1px solid var(--lux-border-1)', background: 'var(--lux-surface-2)' }}>
          <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
            Le password vengono cifrate: la password originale non è mai memorizzata né recuperabile.
          </p>
          <button onClick={onClose}
            className="px-4 py-1.5 text-xs rounded-sm"
            style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
